import { useEffect, useRef, useState } from 'react';
import type { PreviewResponse } from '../../api/types';

type Viewport = 'desktop' | 'mobile';

const widths: Record<Viewport, number> = {
  desktop: 720,
  mobile: 390,
};

/** Until the frame reports its own height. Small enough not to leave a gap on first paint. */
const INITIAL_HEIGHT = 900;

/**
 * Injected into the preview so the frame can tell the parent how tall it is.
 *
 * The alternative is a fixed height, which leaves a screen or two of empty grey below a short email
 * and clips a long one. Measuring from the parent is not possible: the iframe is sandboxed into an
 * opaque origin, and granting it `allow-same-origin` to read a number would also hand the previewed
 * HTML access to this page. `allow-scripts` alone cannot reach the parent DOM at all — postMessage is
 * the only channel it has, which is exactly the amount of capability this needs.
 */
const HEIGHT_REPORTER = `
<script>
(function () {
  var last = 0;
  function report() {
    var height = Math.max(
      document.documentElement.scrollHeight,
      document.body ? document.body.scrollHeight : 0
    );
    if (height !== last) {
      last = height;
      parent.postMessage({ source: 'crh-preview', height: height }, '*');
    }
  }
  window.addEventListener('load', report);
  if (window.ResizeObserver && document.documentElement) {
    new ResizeObserver(report).observe(document.documentElement);
  }
  report();
})();
</script>`;

function withHeightReporter(html: string): string {
  const bodyEnd = html.lastIndexOf('</body>');
  return bodyEnd < 0 ? html + HEIGHT_REPORTER : html.slice(0, bodyEnd) + HEIGHT_REPORTER + html.slice(bodyEnd);
}

interface PreviewPaneProps {
  preview: PreviewResponse | null;
  isLoading: boolean;
  error: string | null;
}

export function PreviewPane({ preview, isLoading, error }: PreviewPaneProps) {
  const [viewport, setViewport] = useState<Viewport>('desktop');
  const [height, setHeight] = useState(INITIAL_HEIGHT);
  const frame = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    function onMessage(event: MessageEvent) {
      // The frame has an opaque origin, so event.origin is "null" and cannot be checked usefully.
      // Identify by the source marker and the shape instead, and only ever accept a number.
      const data = event.data as { source?: unknown; height?: unknown } | null;

      if (
        event.source === frame.current?.contentWindow &&
        data?.source === 'crh-preview' &&
        typeof data.height === 'number' &&
        Number.isFinite(data.height)
      ) {
        setHeight(Math.min(Math.max(Math.ceil(data.height), 200), 40_000));
      }
    }

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  return (
    <section className="preview" aria-label="Email preview">
      <header className="preview__bar">
        <div className="preview__subject">
          <span className="preview__subject-label">Subject</span>
          <strong>{preview?.subject || '—'}</strong>
        </div>

        <div className="preview__controls">
          <div className="segmented" role="group" aria-label="Preview width">
            {(['desktop', 'mobile'] as Viewport[]).map((option) => (
              <button
                key={option}
                type="button"
                className={viewport === option ? 'segmented__item segmented__item--active' : 'segmented__item'}
                aria-pressed={viewport === option}
                onClick={() => setViewport(option)}
              >
                {option === 'desktop' ? 'Desktop' : 'Mobile'}
              </button>
            ))}
          </div>

          {preview && (
            <span className={preview.willBeClippedByGmail ? 'size-pill size-pill--warn' : 'size-pill'}>
              {(preview.htmlSizeBytes / 1024).toFixed(0)} KB
            </span>
          )}

          {isLoading && <span className="preview__spinner" aria-label="Updating preview" />}
        </div>
      </header>

      {preview?.willBeClippedByGmail && (
        <p className="preview__notice">
          This is large enough that Gmail will clip it and hide the end behind a “view entire message”
          link. Shorten the free-text fields, or replace the logo images with smaller files.
        </p>
      )}

      <div className="preview__stage">
        {error ? (
          <p className="preview__empty preview__empty--error">{error}</p>
        ) : preview ? (
          <iframe
            ref={frame}
            className="preview__frame"
            style={{ width: widths[viewport], height }}
            title="Rendered onboarding email"
            srcDoc={withHeightReporter(preview.html)}
            /* Scripts, but no same-origin: the injected reporter can postMessage its height and
               nothing else. It cannot read this page, its cookies or its storage. */
            sandbox="allow-scripts"
          />
        ) : (
          <p className="preview__empty">Fill in the form and the email will appear here.</p>
        )}
      </div>

      <footer className="preview__footnote">
        A browser rendering, with links disabled and anything still to fill in shown in brackets.
        Outlook on Windows renders through Word, which drops rounded corners — create a draft and open
        it there to check the real thing.
      </footer>
    </section>
  );
}
